/**
 * @file CostMarginReport.tsx
 * @brief Super Admin report — cost vs margin (composes analytics revenue APIs).
 *
 * @details
 * No dedicated report endpoint — reuses getRevenueStats / getRevenueTrend
 * (ADR-017: compose when existing APIs suffice).
 *
 * @references
 * - features/analytics/api.ts
 * - docs/product/reporting.md (sa-cost-margin)
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import { percentChange } from "@/features/dashboard/kpiCompare";
import { previousRange } from "@/features/reports-shared/previousRange";
import { Coins, DollarSign, TrendingDown, TrendingUp } from "lucide-react";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import {
  useRevenueStats,
  useRevenueTrend,
} from "@/features/analytics/api";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import { formatMoneyMinor } from "@/shared/utils/formatters";
import { ReportShell } from "./ReportShell";
import { ReportTable } from "./ReportTable";

function defaultRange(): RangeBarValue {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    groupBy: "day",
  };
}

export function CostMarginReport() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultRange());
  const [companyId, setCompanyId] = useState<string | null>(null);

  const baseRange = useMemo(
    () => ({
      startDate: range.startDate,
      endDate: range.endDate,
      companyId: companyId ?? undefined,
    }),
    [range.startDate, range.endDate, companyId],
  );
  const trendRange = useMemo(
    () => ({ ...baseRange, groupBy: range.groupBy }),
    [baseRange, range.groupBy],
  );

  const stats = useRevenueStats({ ...baseRange });
  // The same-length window before, for the +/-% pills (03-10-2026). Money
  // is compared only when both windows lead with the same currency.
  const prevStats = useRevenueStats({ ...baseRange, ...previousRange(baseRange) }).data;
  const trend = useRevenueTrend(trendRange);
  // (24-08-2026) Both feeds are PLATFORM-wide and now come back per currency.
  // Every figure on this page used to be a cross-currency sum printed as "GBP".
  // `stats` leads with its largest currency; the table is filtered to the same
  // one so the KPI row and the period breakdown describe one ledger.
  const currency = stats.data?.currency ?? "GBP";
  const otherCurrencies = (stats.data?.totals_by_currency ?? [])
    .map((t) => t.currency)
    .filter((c) => c && c !== currency);
  const sameCurrency = prevStats != null && (prevStats.currency ?? "GBP") === currency;
  const prevDelta = (now: unknown, before: unknown) =>
    sameCurrency ? percentChange(Number(now ?? 0), Number(before ?? 0)) : undefined;
  const rows = useMemo(
    () =>
      (trend.data ?? []).filter(
        (r) => (r.currency ?? currency) === currency,
      ),
    [trend.data, currency],
  );
  const profit = Number(stats.data?.total_profit_cents ?? 0);

  return (
    <ReportShell
      title="Cost vs margin"
      description="Session revenue vs AI cost. Group-by controls the period table."
      range={range}
      onRangeChange={setRange}
      showCompanyFilter
      companyId={companyId}
      onCompanyChange={setCompanyId}
      exportDisabled={rows.length === 0}
      serverExport={{
        scope: "admin",
        reportKey: "cost-margin",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
          companyId: companyId ?? undefined,
          groupBy: range.groupBy,
        },
      }}
      enablePdf
    >
      <>
        <ReportKpiRow>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Revenue"
              icon={DollarSign}
              value={formatMoneyMinor(stats.data?.total_revenue_cents, currency)}
              hint="Billed / charged"
              delta={prevDelta(stats.data?.total_revenue_cents, prevStats?.total_revenue_cents)}
              isLoading={stats.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Cost"
              icon={Coins}
              value={formatMoneyMinor(stats.data?.total_cost_cents, currency)}
              hint="OpenAI usage cost"
              delta={prevDelta(stats.data?.total_cost_cents, prevStats?.total_cost_cents)}
              deltaGoodWhen="down"
              isLoading={stats.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Profit"
              icon={profit >= 0 ? TrendingUp : TrendingDown}
              tone={profit >= 0 ? "success" : "warning"}
              value={formatMoneyMinor(stats.data?.total_profit_cents, currency)}
              hint="Revenue − cost"
              delta={prevDelta(stats.data?.total_profit_cents, prevStats?.total_profit_cents)}
              isLoading={stats.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Invoices"
              icon={DollarSign}
              value={String(stats.data?.total_invoices ?? 0)}
              hint="Sessions with invoice URL"
              delta={prevDelta(stats.data?.total_invoices, prevStats?.total_invoices)}
              isLoading={stats.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <ReportSectionHeader
          title={`Period breakdown (${range.groupBy}, ${currency})`}
          description={
            otherCurrencies.length > 0
              ? `Same series as Analytics revenue trend. ${currency} only — the platform also bills in ${otherCurrencies.join(", ")}.`
              : "Same series as Analytics revenue trend — exportable as CSV."
          }
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Period",
              { label: "Revenue", align: "right" },
              { label: "Cost", align: "right" },
              { label: "Profit", align: "right" },
            ]}
            isLoading={trend.isLoading}
            empty={rows.length === 0}
          >
            {rows.map((row) => (
              <tr key={row.period} className="hover:bg-secondary/20">
                <td className="px-4 py-2 font-medium">{row.period}</td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatMoneyMinor(row.total_revenue_cents, currency)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatMoneyMinor(row.total_cost_cents, currency)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatMoneyMinor(row.total_profit_cents, currency)}
                </td>
              </tr>
            ))}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
